import React, { useEffect, useState, useCallback } from 'react';
import { adminService } from '../services/adminService';

const PAGE_SIZE = 30;
const STATE_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'declined', label: 'Declined' },
];

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  try {
    return new Date(dateStr).toLocaleString();
  } catch {
    return dateStr;
  }
};

const formatSlot = (slot) => {
  if (!slot || !slot.start || !slot.end) return '—';
  try {
    const start = new Date(slot.start);
    const end = new Date(slot.end);
    return `${start.toLocaleString()} – ${end.toLocaleTimeString()}`;
  } catch {
    return `${slot.start} – ${slot.end}`;
  }
};

const ProposalsList = () => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stateFilter, setStateFilter] = useState('');
  const [offset, setOffset] = useState(0);
  const [actioning, setActioning] = useState(null);

  const loadProposals = useCallback(
    async (resetOffset = true, pageOffset = 0) => {
      setLoading(true);
      setError(null);
      const currentOffset = resetOffset ? 0 : pageOffset;
      try {
        const data = await adminService.getProposals(
          stateFilter || null,
          PAGE_SIZE,
          currentOffset
        );
        if (resetOffset) {
          setItems(data.items || []);
          setOffset(0);
        } else {
          setItems((prev) => [...prev, ...(data.items || [])]);
        }
        setTotal(data.total ?? 0);
        setOffset(currentOffset + (data.items?.length ?? 0));
      } catch (err) {
        setError(err.message || 'Failed to load proposals');
      } finally {
        setLoading(false);
      }
    },
    [stateFilter]
  );

  useEffect(() => {
    loadProposals(true);
  }, [loadProposals]);

  const handleConfirm = async (p, slotIndex) => {
    const slot = p.proposed_slots[slotIndex];
    if (!slot) return;
    setActioning(p.proposal_id);
    try {
      await adminService.confirmProposal(p.proposal_id, {
        start: slot.start,
        end: slot.end,
        provider_id: slot.provider_id,
      });
      await loadProposals(true);
    } catch (err) {
      setError(err.message || 'Failed to confirm');
    } finally {
      setActioning(null);
    }
  };

  const handleDecline = async (p) => {
    setActioning(p.proposal_id);
    try {
      await adminService.declineProposal(p.proposal_id);
      await loadProposals(true);
    } catch (err) {
      setError(err.message || 'Failed to decline');
    } finally {
      setActioning(null);
    }
  };

  const hasMore = offset < total;

  return (
    <div className="max-w-4xl">
      <h1 className="text-xl font-semibold text-gray-100 mb-4">
        Admin – Meeting Proposals
      </h1>
      <p className="text-sm text-gray-400 mb-4">
        Proposals are suggestions, not booked events. Confirm to receive a
        summary and add to your calendar manually.
      </p>

      <div className="mb-4 flex items-center gap-4">
        <label className="text-sm text-gray-400">State:</label>
        <select
          value={stateFilter}
          onChange={(e) => setStateFilter(e.target.value)}
          className="rounded-lg border border-gray-600 bg-gray-800 text-gray-200 px-3 py-1.5 text-sm"
        >
          {STATE_OPTIONS.map((o) => (
            <option key={o.value || 'all'} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      {error && <p className="mb-4 text-red-400 text-sm">{error}</p>}

      <div className="overflow-x-auto rounded-lg border border-gray-700">
        <table className="w-full text-sm text-left">
          <thead className="bg-gray-800 text-gray-300">
            <tr>
              <th className="px-4 py-3">Visitor</th>
              <th className="px-4 py-3">Proposed slots</th>
              <th className="px-4 py-3">State</th>
              <th className="px-4 py-3">Created</th>
              <th className="px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700">
            {items.map((p) => (
              <tr key={p.proposal_id} className="bg-gray-800/50 hover:bg-gray-800">
                <td className="px-4 py-3">
                  <span className="text-gray-200">
                    {p.visitor_display_name || p.visitor_email || '—'}
                  </span>
                  {p.chat_id && (
                    <span className="block text-xs text-gray-500">
                      chat: {p.chat_id}
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-gray-400">
                  <ul className="list-disc list-inside space-y-0.5">
                    {p.proposed_slots?.map((s, i) => (
                      <li key={i}>{formatSlot(s)}</li>
                    ))}
                    {(!p.proposed_slots || p.proposed_slots.length === 0) && '—'}
                  </ul>
                </td>
                <td className="px-4 py-3">
                  <span
                    className={
                      p.state === 'pending'
                        ? 'text-amber-400'
                        : p.state === 'confirmed'
                        ? 'text-green-400'
                        : 'text-gray-500'
                    }
                  >
                    {p.state}
                  </span>
                </td>
                <td className="px-4 py-3 text-gray-500 text-xs">
                  {formatDate(p.created_at)}
                </td>
                <td className="px-4 py-3">
                  {p.state === 'pending' && (
                    <div className="flex flex-wrap gap-2">
                      {p.proposed_slots?.map((slot, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => handleConfirm(p, i)}
                          disabled={actioning === p.proposal_id}
                          className="text-xs px-2 py-1 rounded bg-green-800/60 text-green-300 hover:bg-green-700/60 disabled:opacity-50"
                        >
                          Confirm {i + 1}
                        </button>
                      ))}
                      <button
                        type="button"
                        onClick={() => handleDecline(p)}
                        disabled={actioning === p.proposal_id}
                        className="text-xs px-2 py-1 rounded bg-gray-700 text-gray-300 hover:bg-gray-600 disabled:opacity-50"
                      >
                        Decline
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {loading && items.length === 0 && (
        <p className="py-8 text-gray-400 text-center">Loading proposals...</p>
      )}

      {!loading && items.length === 0 && !error && (
        <p className="py-8 text-gray-400 text-center">No proposals found.</p>
      )}

      {hasMore && !loading && (
        <div className="mt-4">
          <button
            type="button"
            onClick={() => loadProposals(false, offset)}
            className="rounded-lg px-4 py-2 text-sm font-medium bg-gray-700 text-gray-200 hover:bg-gray-600"
          >
            Load more
          </button>
        </div>
      )}
    </div>
  );
};

export default ProposalsList;
